"use client"

import { useEffect, useState } from "react"
import Logo from "../../assets/images/logo/hazer-logo.png"
import apiUrls from "../../config/api-urls"
import { apiRequest } from "../../services/api-client"
import tokenService from "../../services/token-service"

const RegisterModal = ({ isOpen, onClose, onSuccess, onSwitchToLogin }) => {

  const [roles, setRoles] = useState([])

  const [formData, setFormData] = useState({
    fullName: "",
    phoneNumber: "",
    role: "",
    email: "",
    promoCode: "",
    password: "",
    confirmPassword: "",
  })
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await apiRequest("GET", apiUrls.roles.getAll)
        if (response.data?.data) {
          const filteredRoles = response.data.data.filter(
            (role) => !["admin", "super_admin"].includes(role.name)
          )
          setRoles(filteredRoles)
        }
      } catch (err) {
      }
    }

    if (isOpen) {
      fetchRoles()
    }
  }, [isOpen])

  const formatRoleName = (roleName) => {
    return roleName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  }

  const getPasswordRules = (password) => ({
    minLength: password.length >= 6,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecialChar: /[@$!%*?&]/.test(password),
  })

  const validateForm = () => {
    if (!formData.fullName.trim()) return "Full Name is required"

    if (!/^[0-9]{10,15}$/.test(formData.phoneNumber)) {
      return "Invalid phone number"
    }

    if (!formData.role) return "Role is required"

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      return "Invalid email format"
    }

    const passwordRules = getPasswordRules(formData.password)
    if (!passwordRules.minLength) return "Password must be at least 6 characters long."
    if (!passwordRules.hasUpperCase) return "Password must contain at least one uppercase letter."
    if (!passwordRules.hasLowerCase) return "Password must contain at least one lowercase letter."
    if (!passwordRules.hasNumber) return "Password must contain at least one number."
    if (!passwordRules.hasSpecialChar) return "Password must contain at least one special character."

    if (formData.password !== formData.confirmPassword) {
      return "Passwords do not match"
    }

    if (!agreedToTerms) {
      return "Please agree to the Terms & Conditions"
    }

    return ""
  }

  const handleChange = (e) => {
    if (e.target.name === "role") {
      setFormData((prev) => ({
        ...prev,
        role: e.target.value,
        email: prev.email,
        promoCode: prev.promoCode,
      }))
      setError("")
      return
    }

    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
    setError("") // Clear error on input change
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const validationError = validateForm()
    if (validationError) {
      setError(validationError)
      return
    }

    setLoading(true)
    setError("")

    try {
      const payload = {
        fullName: formData.fullName,
        phoneNumber: formData.phoneNumber,
        password: formData.password,
        role: formData.role,
      }

      if (formData.email) payload.email = formData.email
      if (formData.promoCode) payload.promoCode = formData.promoCode

      const response = await apiRequest("POST", apiUrls.auth.register, payload)

      if (response?.data?.data?.user?.token) {
        // If backend auto-logs in and returns token
        tokenService.setToken(response.data.data.user.token)
        tokenService.setUserDetails(response.data.data.user)

        // Store user in localStorage for authentication check
        localStorage.setItem("authUser", JSON.stringify(response.data.data.user))

        // Close modal
        onClose && onClose()

        // Reset form
        setFormData({
          fullName: "",
          phoneNumber: "",
          role: "",
          email: "",
          promoCode: "",
          password: "",
          confirmPassword: "",
        })
        setAgreedToTerms(false)

        // Reload to refresh user data
        setTimeout(() => {
          window.location.reload()
        }, 1500)
      } else {
        // If backend doesn't auto-login, switch to login modal
        onClose && onClose()
        onSwitchToLogin && onSwitchToLogin()

        // Reset form
        setFormData({
          fullName: "",
          phoneNumber: "",
          role: "",
          email: "",
          promoCode: "",
          password: "",
          confirmPassword: "",
        })
        setAgreedToTerms(false)
      }
    } catch (err) {
      console.error("Signup failed:", err)
      setError(err.response?.data?.error?.message || err.response?.data?.message || "Registration failed. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start md:items-center justify-center z-50 p-4 overflow-y-auto overflow-x-hidden">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[78vh] overflow-y-auto overflow-x-hidden">
        <div className="flex flex-col md:flex-row min-h-[460px]">
          {/* Left */}
          <div className="hidden md:flex md:w-1/2 flex-col items-center justify-center text-center p-6 bg-gradient-to-br from-[#ff9906] to-[#f79d2d] text-white">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Welcome to Hazer Globals</h2>
            <p className="text-sm md:text-lg mb-6">We make it easy for everyone to access their account.</p>

            <div className="bg-white rounded-2xl mt-4">
              <img src={Logo} alt="Hazer Logo" className="mt-6 w-32 h-32 md:w-40 md:h-40 rounded-lg shadow-lg" />
            </div>
          </div>

          {/* Right */}
          <div className="w-full md:w-1/2 p-6 md:p-8 relative flex flex-col">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold"
              aria-label="Close"
            >
              ×
            </button>

            <div className="max-w-sm mx-auto w-full flex flex-col flex-1 min-h-0">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Register</h2>

              {error && <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>}

              <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                <div className="space-y-4 flex-1 min-h-0 overflow-x-hidden pr-1">
                  <input
                    type="text"
                    name="fullName"
                    placeholder="Full Name"
                    value={formData.fullName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff8c00] focus:border-transparent outline-none"
                    required
                  />

                  <input
                    type="tel"
                    name="phoneNumber"
                    placeholder="Phone Number"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff8c00] focus:border-transparent outline-none"
                    required
                  />

                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff8c00] focus:border-transparent outline-none bg-white text-gray-700"
                    required
                  >
                    <option value="" disabled>
                      Select User Type
                    </option>
                    {roles.map((role) => (
                      <option key={role._id} value={role.name}>
                        {formatRoleName(role.name)}
                      </option>
                    ))}
                  </select>

                  {(formData.role?.toLowerCase() === "customer" ||
                    formData.role?.toLowerCase() === "handyman") && (
                      <>
                        <input
                          type="email"
                          name="email"
                          placeholder="Email (optional)"
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff8c00] focus:border-transparent outline-none"
                        />

                        <input
                          type="text"
                          name="promoCode"
                          placeholder="Marketing Executive Promo Code"
                          value={formData.promoCode}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff8c00] focus:border-transparent outline-none"
                        />
                      </>
                    )}

                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Password"
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff8c00] focus:border-transparent outline-none pr-12"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      aria-label="Toggle password visibility"
                    >
                      {showPassword ? "👁️" : "👁️‍🗨️"}
                    </button>
                  </div>

                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      placeholder="Confirm Password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff8c00] focus:border-transparent outline-none pr-12"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      aria-label="Toggle confirm password visibility"
                    >
                      {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
                    </button>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="terms"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="mt-1 mr-2"
                      required
                    />
                    <label htmlFor="terms" className="text-sm text-gray-600">
                      By Signing Up, I Agree with{" "}
                      <a href="#" className="text-[#ff8c00] hover:underline">
                        Terms & Conditions
                      </a>
                    </label>
                  </div>
                </div>

                <div className="space-y-3 pt-4 bg-white">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#ff8c00] text-white py-3 rounded-lg font-semibold hover:bg-[#e67c00] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Registering..." : "Register"}
                  </button>

                  <button
                    type="button"
                    onClick={onSwitchToLogin}
                    className="w-full bg-white text-[#ff8c00] py-3 rounded-lg font-semibold border border-[#ff8c00] hover:bg-[#ff8c00] hover:text-white transition-colors"
                  >
                    Login
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RegisterModal